import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';

const CAPTCHA_LENGTH = 6;
// Ambiguous characters (0/O, 1/l/I) are excluded so the rendered text stays legible
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
const CANVAS_WIDTH = 180;
const CANVAS_HEIGHT = 56;

const generateCaptchaText = () => {
  let text = ''
  for (let i = 0; i < CAPTCHA_LENGTH; i++) {
    text += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)]
  }
  return text
}

const drawCaptcha = (canvas, text) => {
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas

  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#eef2fa'
  ctx.fillRect(0, 0, width, height)

  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = `hsla(${Math.random() * 360}, 60%, 55%, 0.5)`
    ctx.lineWidth = 1 + Math.random()
    ctx.beginPath()
    ctx.moveTo(Math.random() * width, Math.random() * height)
    ctx.lineTo(Math.random() * width, Math.random() * height)
    ctx.stroke()
  }

  for (let i = 0; i < 35; i++) {
    ctx.fillStyle = `hsla(${Math.random() * 360}, 50%, 50%, 0.4)`
    ctx.beginPath()
    ctx.arc(Math.random() * width, Math.random() * height, 1.2, 0, Math.PI * 2)
    ctx.fill()
  }

  const charSlot = width / (text.length + 1)
  text.split('').forEach((char, i) => {
    ctx.save()
    ctx.translate(charSlot * (i + 0.85), height / 2 + (Math.random() * 10 - 5))
    ctx.rotate((Math.random() * 40 - 20) * Math.PI / 180)
    ctx.font = `bold ${height * 0.55}px 'Trebuchet MS', sans-serif`
    ctx.fillStyle = `hsl(${205 + Math.random() * 40}, 55%, 32%)`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(char, 0, 0)
    ctx.restore()
  })
}

/**
 * @description Self-contained, canvas-rendered captcha challenge. Regenerates its own
 * code and only reports pass/fail to the parent via onVerify - no external service
 * or network call is involved, so it only deters unsophisticated scripted bots.
 */
const Captcha = React.forwardRef(function Captcha({ onVerify }, ref) {
  const canvasRef = React.useRef(null)
  const codeRef = React.useRef('')
  const [input, setInput] = React.useState('')

  const refresh = React.useCallback(() => {
    codeRef.current = generateCaptchaText()
    setInput('')
    onVerify(false)

    if (canvasRef.current) {
      drawCaptcha(canvasRef.current, codeRef.current)
    }
  }, [onVerify])

  React.useImperativeHandle(ref, () => ({ refresh }), [refresh])

  React.useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleInputChange = (event) => {
    const value = event.target.value
    setInput(value)
    onVerify(value.trim() === codeRef.current)
  }

  return (
    <Box className='signin-captcha'>
      <Box className='signin-captcha-canvas-row'>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          role='img'
          aria-label='Captcha challenge image'
          className='signin-captcha-canvas'
        />
        <IconButton
          onClick={refresh}
          size='small'
          aria-label='Get a new captcha'
          title='Get a new captcha'
        >
          <RefreshIcon fontSize='small' />
        </IconButton>
      </Box>
      <TextField
        fullWidth
        margin='normal'
        id='captchaInput'
        name='captchaInput'
        label='Enter the code shown above'
        autoComplete='off'
        value={input}
        onChange={handleInputChange}
      />
    </Box>
  )
})

export default Captcha
